/**
 * Data fetching.
 *
 * Everything the Console shows is server state, so it is fetched, cached and
 * invalidated rather than copied into component state. The defaults are tuned
 * for an operator console: refetch when a window regains focus (somebody has
 * been in a terminal), and never retry a 4xx, because a 403 or a 404 will not
 * become a 200 by asking again.
 */

import { QueryClient } from "@tanstack/react-query";
import { ApiError, get } from "@/api/client";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/** The query key *is* the path, so invalidating a prefix invalidates its pages. */
export function pathKey(path: string): string[] {
  return path.replace(/^\//, "").split("/");
}

export function fetcher<T>(path: string) {
  return { queryKey: pathKey(path), queryFn: () => get<T>(path) };
}

/** Invalidate everything under a path prefix — `/instances` covers its details. */
export function invalidate(prefix: string) {
  return queryClient.invalidateQueries({ queryKey: pathKey(prefix) });
}

// Query keys are paths, and the same path answers differently inside a partner:
// nothing read as one organisation may stay on screen as another's.
if (typeof window !== "undefined") {
  window.addEventListener("mdc:working-org", () => { queryClient.removeQueries(); });
}
